import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { ScheduleItem } from '../types';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { CalendarDays, Clock, MapPin, Edit2, Trash2, PlusCircle, Globe, ScrollText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';

const INITIAL_SCHEDULE: Partial<ScheduleItem> = {
  start_time: '12:00',
  title: '',
  location: '',
  is_public: true
};

export default function Schedule() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<ScheduleItem>>(INITIAL_SCHEDULE);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchItems = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const { data } = await supabase.from('schedule_items').select('*').eq('wedding_id', wedding.id).order('start_time');
    if (data) setItems(data);
  };

  useEffect(() => {
    fetchItems();
  }, [wedding]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    if (formData.id) {
      const { error } = await supabase.from('schedule_items').update(formData).eq('id', formData.id);
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    } else {
      const { error } = await supabase.from('schedule_items').insert({ ...formData, wedding_id: wedding.id });
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    }
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    fetchItems();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('schedule_items').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchItems();
  };

  if (!wedding) return null;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('nav.schedule')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-2">Naplánujte průběh vašeho svatebního dne.</p>
        </div>
        <Button 
          onClick={() => { setFormData(INITIAL_SCHEDULE); setIsModalOpen(true); }}
          variant="secondary"
          icon={<PlusCircle size={18} />}
        >
          {t('common.add')}
        </Button>
      </div>

      <div className="space-y-6 relative before:absolute before:inset-y-4 before:left-[11px] before:w-px before:bg-slate-200 dark:before:bg-slate-800">
        {items.length === 0 ? (
          <div className="pl-24">
            <EmptyState 
                icon={CalendarDays} 
                title="Žádný harmonogram" 
                description="Začněte přidávat body programu."
                action={{ 
                    label: t('common.add'), 
                    onClick: () => { setFormData(INITIAL_SCHEDULE); setIsModalOpen(true); },
                    icon: PlusCircle
                }}
            />
          </div>
        ) : (
          items.map(item => (
            <div key={item.id} className="relative pl-24 group">
               <div className="absolute left-[28px] top-1 w-14 text-right font-medium text-slate-900 dark:text-white text-sm tabular-nums">
                  {item.start_time.slice(0, 5)}
               </div>
               <div className="absolute left-[5px] top-2 w-3 h-3 bg-white dark:bg-slate-900 border-2 border-brand-500 rounded-full z-10 shrink-0" aria-hidden />
               
               <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-ds-border transition-all">
                  <div className="flex justify-between items-start">
                     <div>
                        <h3 className="text-ds-h3 text-slate-900 dark:text-white mb-1 flex items-center gap-2">
                           {item.title}
                           {item.is_public && <Globe size={14} className="text-slate-400" />}
                           {item.ceremony_step_id && (
                              <span className="text-ds-meta text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded flex items-center gap-1">
                                 <ScrollText size={12} /> Z Obřadu
                              </span>
                           )}
                        </h3>
                        {item.location && (
                           <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mb-2">
                              <MapPin size={14} /> {item.location}
                           </p>
                        )}
                        {item.notes && <p className="text-sm text-slate-600 dark:text-slate-300 italic">{item.notes}</p>}
                        {item.ceremony_step_id && (
                           <p className="text-xs text-slate-400 mt-2">
                              Úprava pouze v sekci <Link to="ceremony#prubeh" className="text-brand-600 hover:underline font-medium">Svatební obřad → Časová osa</Link>.
                           </p>
                        )}
                     </div>
                     {!item.ceremony_step_id && (
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                           <button type="button" onClick={() => { setFormData(item); setIsModalOpen(true); }} className="p-2 text-slate-400 hover:text-brand-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                              <Edit2 size={16} />
                           </button>
                           <button type="button" onClick={() => setDeleteId(item.id)} className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                              <Trash2 size={16} />
                           </button>
                        </div>
                     )}
                  </div>
               </div>
            </div>
          ))
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? 'Upravit bod' : 'Nový bod programu'}>
         <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
               <div>
                  <label className="text-ds-meta text-slate-500 mb-2 block">Čas</label>
                  <input 
                     type="time" 
                     required
                     value={formData.start_time || ''} 
                     onChange={e => setFormData({ ...formData, start_time: e.target.value })}
                     className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
                  />
               </div>
               <div className="col-span-2">
                  <label className="text-ds-meta text-slate-500 mb-2 block">Název</label>
                  <input 
                     type="text" 
                     required
                     value={formData.title || ''} 
                     onChange={e => setFormData({ ...formData, title: e.target.value })}
                     className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
                  />
               </div>
            </div>
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Místo</label>
               <input 
                  type="text" 
                  value={formData.location || ''} 
                  onChange={e => setFormData({ ...formData, location: e.target.value })}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
               />
            </div>
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Poznámka</label>
               <textarea 
                  value={formData.notes || ''} 
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
               />
            </div>
            <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 cursor-pointer">
               <input 
                  type="checkbox" 
                  checked={formData.is_public} 
                  onChange={e => setFormData({...formData, is_public: e.target.checked})}
                  className="w-5 h-5 rounded text-brand-600 focus:ring-brand-500"
               />
               <span className="font-bold text-sm text-slate-700 dark:text-slate-300">Zobrazit na webu pro hosty</span>
            </label>
            <button type="submit" className="w-full bg-brand-600 text-white font-bold py-4 rounded-xl hover:bg-brand-700 transition-colors">
               {t('common.save')}
            </button>
         </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onCancel={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} description={t('common.confirm_delete')} />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}